"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { DurableResult } from "@/features/home-v2/money/DurableResult";
import { TransactionReview } from "@/features/home-v2/money/TransactionReview";
import { SectionBoundary } from "@/features/home-v2/states/SectionBoundary";
import { Dialog } from "@/features/home-v2/ui";
import { Act, Foot, Steps } from "@/features/wallet/v2/dialogs/kit";
import type { FlowModel } from "../../flows/shared";
import { useFlowSteps } from "../../flows/useFlowSteps";
import { nftReaderRows } from "./nftReaderRows";
import { SignPanel } from "./SignPanel";
import "@/features/home-v2/money/money.css";
import "@/features/wallet/v2/wallet.css";
import "@/features/wallet/v2/dialogs/dialogs.css";
import "./nft-dialogs.css";

/**
 * ONE MONEY PATH ON THE NFT PAGE, in the wallet dialogs' grammar: a gilt step
 * ruler (details → review → receipt; review → receipt when there is nothing to
 * enter), the flow's own question under it, the review as the holder reads it
 * with the irreversibility stated, the user's own wallet under the review
 * when the backend asked for a round, the stamped receipt, and a footer that
 * says why it is blocked. Every rule is the flow hook's; `useFlowSteps` is the
 * legacy dialog's machine. `overlay` (a list opened in place) takes the body.
 */
export function FlowDialog(t0) {
  const $ = _c(57);
  const {
    m,
    input,
    overlay
  } = t0;
  const isFa = m.lang === "fa";
  let t1;
  if ($[0] !== isFa) {
    t1 = (fa, en) => isFa ? fa : en;
    $[0] = isFa;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t = t1;
  const s = useFlowSteps(m);
  let t2;
  if ($[2] !== m.hasInput || $[3] !== t) {
    t2 = m.hasInput ? [t("\u062C\u0632\u0626\u06CC\u0627\u062A", "Details"), t("\u0628\u0627\u0632\u0628\u06CC\u0646\u06CC", "Review"), t("\u0631\u0633\u06CC\u062F", "Receipt")] : [t("\u0628\u0627\u0632\u0628\u06CC\u0646\u06CC", "Review"), t("\u0631\u0633\u06CC\u062F", "Receipt")];
    $[2] = m.hasInput;
    $[3] = t;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const steps = t2;
  let t3;
  if ($[5] !== m.hasInput) {
    t3 = m.hasInput ? {
      input: 0,
      review: 1,
      result: 2
    } : {
      input: 0,
      review: 0,
      result: 1
    };
    $[5] = m.hasInput;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const at = t3[s.step];
  const danger = m.confirmColor === "danger";
  let t4;
  if ($[7] !== input || $[8] !== m.handoff || $[9] !== m.irreversibility || $[10] !== m.lang || $[11] !== m.networkLabel || $[12] !== m.rows || $[13] !== s.result || $[14] !== s.step || $[15] !== t) {
    t4 = s.step === "input" ? <div className="nd-form">{input}</div> : s.step === "review" ? <><TransactionReview title={t("\u0628\u0627\u0632\u0628\u06CC\u0646\u06CC \u062A\u0631\u0627\u06A9\u0646\u0634", "Transaction review")} rows={nftReaderRows(m.rows, m.lang)} irreversibility={m.irreversibility} /><SignPanel handoff={m.handoff} networkLabel={m.networkLabel} lang={m.lang} /></> : s.result ? <><DurableResult lang={m.lang} outcome={s.result.outcome === "success" ? "success" : "failure"} title={s.result.title} description={s.result.description} refs={s.result.refs} labels={{
        copy: t("\u06A9\u067E\u06CC", "Copy"),
        open: t("\u0628\u0627\u0632 \u06A9\u0631\u062F\u0646", "Open")
      }} onCopy={value => {
        navigator.clipboard?.writeText(value);
        toast.success(t("\u06A9\u067E\u06CC \u0634\u062F", "Copied"));
      }} /><p className="md-after">{t("\u0627\u06CC\u0646 \u0646\u062A\u06CC\u062C\u0647 \u067E\u0633 \u0627\u0632 \u0628\u0633\u062A\u0646 \u0627\u06CC\u0646 \u067E\u0646\u062C\u0631\u0647 \u0647\u0645 \u0631\u0648\u06CC \u0647\u0645\u06CC\u0646 \u0635\u0641\u062D\u0647 \u0628\u0627\u0642\u06CC \u0645\u06CC\u200C\u0645\u0627\u0646\u062F.", "This result stays on the page after you close this dialog.")}</p></> : null;
    $[7] = input;
    $[8] = m.handoff;
    $[9] = m.irreversibility;
    $[10] = m.lang;
    $[11] = m.networkLabel;
    $[12] = m.rows;
    $[13] = s.result;
    $[14] = s.step;
    $[15] = t;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const body = t4;
  let t5;
  if ($[17] !== danger || $[18] !== m.blockedReason || $[19] !== m.confirmConsequence || $[20] !== m.confirmLabel || $[21] !== m.confirmLoadingLabel || $[22] !== m.hasInput || $[23] !== m.onClose || $[24] !== overlay || $[25] !== s || $[26] !== t) {
    t5 = overlay ? null : s.step === "result" ? <Foot><Act onClick={m.onClose}>{t("\u0628\u0633\u062A\u0646", "Close")}</Act></Foot> : s.step === "input" ? <Foot reason={m.blockedReason}><Act ghost={true} onClick={m.onClose}>{t("\u0627\u0646\u0635\u0631\u0627\u0641", "Cancel")}</Act><Act onClick={() => s.setStep("review")} disabled={s.isContinueDisabled}>{t("\u0628\u0631\u0631\u0633\u06CC \u0648 \u0627\u062F\u0627\u0645\u0647", "Review")}</Act></Foot> : <Foot reason={m.blockedReason} busy={s.pending}>{m.hasInput ? <Act ghost={true} onClick={() => s.setStep("input")}>{t("\u0628\u0627\u0632\u06AF\u0634\u062A", "Back")}</Act> : <Act ghost={true} onClick={m.onClose}>{t("\u0627\u0646\u0635\u0631\u0627\u0641", "Cancel")}</Act>}<span className={danger ? "nd-act nd-act--danger" : "nd-act"}><Act onClick={() => void s.handleConfirm()} disabled={s.isContinueDisabled} busy={s.pending} busyLabel={m.confirmLoadingLabel ?? t("\u062F\u0631 \u062D\u0627\u0644 \u0627\u0631\u0633\u0627\u0644", "Submitting")} ariaLabel={`${m.confirmLabel} — ${m.confirmConsequence}`}>{m.confirmLabel}</Act></span></Foot>;
    $[17] = danger;
    $[18] = m.blockedReason;
    $[19] = m.confirmConsequence;
    $[20] = m.confirmLabel;
    $[21] = m.confirmLoadingLabel;
    $[22] = m.hasInput;
    $[23] = m.onClose;
    $[24] = overlay;
    $[25] = s;
    $[26] = t;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  const foot = t5;
  const t6 = m.onClose;
  const t7 = m.title;
  let t8;
  if ($[28] !== t) {
    t8 = t("\u0628\u0633\u062A\u0646", "Close");
    $[28] = t;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const t9 = s.pending;
  let t10;
  if ($[30] !== t) {
    t10 = t("\u0627\u06CC\u0646 \u0628\u062E\u0634 \u0628\u0627\u0631\u06AF\u0630\u0627\u0631\u06CC \u0646\u0634\u062F", "This section could not be loaded");
    $[30] = t;
    $[31] = t10;
  } else {
    t10 = $[31];
  }
  let t11;
  if ($[32] !== t) {
    t11 = t("\u0686\u06CC\u0632\u06CC \u0627\u0631\u0633\u0627\u0644 \u0646\u0634\u062F\u0647 \u0627\u0633\u062A. \u067E\u0646\u062C\u0631\u0647 \u0631\u0627 \u0628\u0628\u0646\u062F\u06CC\u062F \u0648 \u062F\u0648\u0628\u0627\u0631\u0647 \u0628\u0627\u0632 \u06A9\u0646\u06CC\u062F.", "Nothing was submitted. Close this dialog and open it again.");
    $[32] = t;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t) {
    t12 = t("\u062A\u0644\u0627\u0634 \u062F\u0648\u0628\u0627\u0631\u0647", "Try again");
    $[34] = t;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== at || $[37] !== body || $[38] !== m.description || $[39] !== overlay || $[40] !== s.step || $[41] !== steps || $[42] !== t) {
    t13 = overlay ?? <><Steps steps={steps} at={at} label={t("\u0645\u0631\u0627\u062D\u0644", "Steps")} />{m.description && s.step !== "result" ? <p className="nd-lead">{m.description}</p> : null}{body}</>;
    $[36] = at;
    $[37] = body;
    $[38] = m.description;
    $[39] = overlay;
    $[40] = s.step;
    $[41] = steps;
    $[42] = t;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  let t14;
  if ($[44] !== s.step || $[45] !== t10 || $[46] !== t11 || $[47] !== t12 || $[48] !== t13) {
    t14 = <SectionBoundary title={t10} description={t11} retryLabel={t12} resetKey={s.step}>{t13}</SectionBoundary>;
    $[44] = s.step;
    $[45] = t10;
    $[46] = t11;
    $[47] = t12;
    $[48] = t13;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== foot || $[51] !== m.onClose || $[52] !== m.title || $[53] !== s.pending || $[54] !== t14 || $[55] !== t8) {
    t15 = <Dialog open={true} onClose={t6} title={t7} closeLabel={t8} className="md-dialog nd-dialog" footer={foot} busy={t9}>{t14}</Dialog>;
    $[50] = foot;
    $[51] = m.onClose;
    $[52] = m.title;
    $[53] = s.pending;
    $[54] = t14;
    $[55] = t8;
    $[56] = t15;
  } else {
    t15 = $[56];
  }
  return t15;
}